import { Flex, Text, Image, Box, Button } from "@chakra-ui/react";
import { FunnelComponentProps, FunnelContextArea, funnelToEventContextArea } from "../../funnel/types";
import { ReleasePurchaseData } from "./ReleasePurchaseData";
import { useTranslation } from "react-i18next";
import { useUserData } from "../../../hooks/useUserData";
import { DiscountBanner } from "./DiscountBanner";
import { CloseIcon } from "@chakra-ui/icons";
import { useEffect, useRef } from "react";
import { useIsVisible } from "../../../hooks/useIsVisible";
import { EventTypes } from "../../../screens/root/api/eventsApi";
import { ReleasePriceLabel } from "../../cards/ReleasePriceLabel";
import { ReleaseActionsButtons } from "../../releases/components/ReleaseActionsButtons";
import { CollectibleBadge } from "../../cards/components/CollectibleBadge";
import { CollectibleIndicator } from "./CollectibleIndicator";
import { usePageLayout } from "../../../screens/reader/hooks/usePageLayout";
import { PAGE_LAYOUT_TYPES } from "../../../screens/reader/constants";
import { FunnelSteps } from "../../funnel/FunnelSteps";

export function PrePaywallModalHeader({
  modalContext,
  onClose,
}: FunnelComponentProps) {
  const isContextWithNoX = modalContext === FunnelContextArea.verticalReader;

  if (isContextWithNoX) {
    return null;
  }
  return (
    <Flex justifyContent="flex-end" width="100%" padding="16px 24px">
      <Button
        gc-testing-id="btnCloseSubscriptionModal"
        variant="iconOnly"
        onClick={onClose}
        minWidth={0}
      >
        <CloseIcon color="dune.100" />
      </Button>
    </Flex>
  );
}

export function PrePaywallModalBody({
  modalContext,
  release,
  updateFunnelConfig,
  shouldShowSteps,
  stepCount,
  currentStep,
  stepText,
  stepDisplayText,
  showStepIndicators,
  sendEvent,
}: FunnelComponentProps) {
  const { t } = useTranslation();
  const { userData } = useUserData();
  const { premium_only } = release ?? {};

  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(ref, 0.5);
  const alreadyTracked = useRef(false);
  const { pageLayout } = usePageLayout();

  useEffect(() => {
    if (sendEvent && isVisible && !alreadyTracked.current) {
      sendEvent(EventTypes.viewed_alc_offer);
      alreadyTracked.current = true;
    }
  }, [isVisible]);

  if (!release) return null;
  return (
    <Flex flexDirection="column" gap="32px" padding="24px" ref={ref}>
      {shouldShowSteps ? (
        <FunnelSteps
          stepCount={stepCount}
          currentStep={currentStep}
          stepText={stepText}
          text={stepDisplayText}
          hideProgressIndicators={!showStepIndicators}
        />
      ) : (
        <Text
          variant="title"
          color="dune.dune"
          fontWeight="600"
          lineHeight="130%"
          textAlign="center"
          sx={{
            textWrap: "balance",
          }}
        >
          {t("components.payments.prePaywall.buyTheBookToContinueReading")}
        </Text>
      )}
      <Flex
        alignSelf="center"
        flexDirection="row"
        width="fit-content"
        paddingTop="16px"
      >
        <Image
          borderRadius="10px"
          height="201px"
          aspectRatio={0.646}
          src={release.thumbnail_url}
          alt="Release thumbnail"
          mr={6}
        />
        <Flex flexDirection="column" width="100%" gap="20px">
          <CollectibleBadge />
          <ReleasePurchaseData
            pagesTextSuffix={t("components.payments.purchaseRelease.pages")}
            release={release}
          />
          <ReleasePriceLabel release={release} fontSize="16px" />
        </Flex>
      </Flex>
      <Box width="100%" height="1px" bg="transparent.white.10" />
      <Flex gap="8px" flexDirection="column" alignItems="center">
        <DiscountBanner release={release} isGold={userData?.gold ?? false} />
        {premium_only ? <CollectibleIndicator /> : null}
      </Flex>

      <ReleaseActionsButtons
        release={release}
        setFunnelConfig={updateFunnelConfig}
        hidePreviewButton
        buttonSize="lg"
        contextArea={modalContext}
        eventContextArea={funnelToEventContextArea(modalContext)}
        contextualButtonDirection={
          pageLayout === PAGE_LAYOUT_TYPES.vertical ? "top" : "bottom"
        }
        escapeContainerBounds={true}
        enableFlipAndPreventOverflow={true}
      />
    </Flex>
  );
}
